import { z } from 'zod';
import { EmptyValidationMessage } from '@ninja/utilities';
import { HasEmiratesResponse, PlanCategory, PolicyType, Relationship, SalaryBands } from '../../../../../common';
import { useCountries } from '../../../../app-init-data';
import { prepareFieldError } from '../../../members/members-form/helpers/add-field-error';
import {
	disableSalaryBandField,
	includeSalaryBandField,
	includeVisaFileNumberField,
	showInsuranceConinuityUpload,
	showOccupationField,
	showUploadLabourContractScan,
} from '../../utilities';
import {
	useCommonFieldsStructure,
	useHasEmiratesIdFieldsStructure,
	useMemberDetailsFieldsStructure,
} from '../fields-structures';
import { DocumentsForm } from '../types';

type Props = {
	members: {
		memberId: string;
		relationshipTypeId?: number;
		salaryBandId?: SalaryBands;
		nationalityId?: number;
		emirateId?: number;
	}[];
	policyType?: PolicyType;
	planCategory?: PlanCategory;
	formValues: DocumentsForm;
	isSponsorLaborContractRequired?: boolean;
};

const useGetValidationSchema = (isEidFlow?: boolean) => {
	const { getValidations: getCommonValidationSchema, validate: validateCommon } = useCommonFieldsStructure();
	const { getValidationSchema: getHasEmiratesIdValidationSchema, validate: validateHasEmiratesId } =
		useHasEmiratesIdFieldsStructure();
	const { getValidations: getMemberDetailsValidationSchema, validate: validateMemberDetails } =
		useMemberDetailsFieldsStructure();

	const { getCountryById } = useCountries();
	const getValidationSchema = (props: Props) => {
		const { members, policyType, planCategory, formValues, isSponsorLaborContractRequired = false } = props;
		const passportNumbers = members.map((m) => formValues[m.memberId].passportNumber);
		const visaFileNumbers = members.map((m) => formValues[m.memberId].visaFileNumber);
		const unifiedNumbers = members
			.filter((m) => formValues[m.memberId].hasEmiratesId === HasEmiratesResponse.No)
			.map((m) => formValues[m.memberId].unifiedNumber);
		const emiratesIds = members
			.filter((m) => formValues[m.memberId].hasEmiratesId === HasEmiratesResponse.Yes)
			.map((m) => formValues[m.memberId].emiratesId);

		return z.object(
			members.reduce(
				(acc, { memberId, relationshipTypeId, nationalityId }) => ({
					...acc,
					[memberId]: getCommonValidationSchema()
						.merge(getHasEmiratesIdValidationSchema())
						.merge(getMemberDetailsValidationSchema())
						.merge(
							z.object({
								passportFile: z.string({ required_error: EmptyValidationMessage }),
								visaFile: z.string({ required_error: EmptyValidationMessage }),
								labourContractFile: z.string({ required_error: EmptyValidationMessage }),
								insuranceContinuityFile: z.string({ required_error: EmptyValidationMessage }),
							})
						)
						.partial()
						.superRefine((memberDocumentFormValue, ctx) => {
							const addFieldError = prepareFieldError(ctx);
							const validateSalaryBand =
								includeSalaryBandField(relationshipTypeId) && !disableSalaryBandField(policyType, relationshipTypeId);
							const validateOccupation = showOccupationField(policyType, relationshipTypeId);

							const passportNumberUseCount = passportNumbers.filter(
								(p) => memberDocumentFormValue.passportNumber === p
							).length;
							const visaFileNumberUseCount = visaFileNumbers.filter(
								(v) => memberDocumentFormValue.visaFileNumber === v
							).length;
							const unifiedNumberUseCount = unifiedNumbers.filter(
								(u) => memberDocumentFormValue.unifiedNumber === u
							).length;
							const emiratesIdUseCount = emiratesIds.filter((e) => memberDocumentFormValue.emiratesId === e).length;

							validateCommon(
								memberDocumentFormValue,
								{
									passportNumberUseCount,
									visaFileNumberUseCount,
									validateSalaryBand,
									nationalityId,
									validatedOccupationId: validateOccupation,
									occupationId: memberDocumentFormValue.occupationId,
								},
								addFieldError
							);
							validateHasEmiratesId(memberDocumentFormValue, addFieldError);
							validateMemberDetails(
								memberDocumentFormValue,
								{ unifiedNumberUseCount, emiratesIdUseCount },
								addFieldError,
								isEidFlow
							);

							if (!memberDocumentFormValue.passportFile) {
								addFieldError({ field: 'passportFile', message: EmptyValidationMessage });
							}

							if (includeVisaFileNumberField(getCountryById(nationalityId!)) && !memberDocumentFormValue.visaFile) {
								addFieldError({ field: 'visaFile', message: EmptyValidationMessage });
							}

							if (
								!memberDocumentFormValue.labourContractFile &&
								showUploadLabourContractScan(
									policyType,
									relationshipTypeId,
									planCategory,
									isSponsorLaborContractRequired
								)
							) {
								addFieldError({ field: 'labourContractFile', message: EmptyValidationMessage });
							}

							const member = members.find((m) => m.memberId === memberId);

							if (
								!memberDocumentFormValue.insuranceContinuityFile &&
								showInsuranceConinuityUpload(
									policyType,
									member?.relationshipTypeId === Relationship.Self,
									member?.emirateId
								)
							) {
								addFieldError({ field: 'insuranceContinuityFile', message: EmptyValidationMessage });
							}
						}),
				}),
				{}
			)
		);
	};

	return getValidationSchema;
};

export default useGetValidationSchema;
